/**
 * One tab per place: the key no two primary tabs may share.
 *
 * With no tab strip there is no close button, so a duplicate tab could never be
 * dismissed. Opening a place that a tab already holds therefore reuses that tab.
 */
import { destinationForPath } from '@shared/navigation/destinations';

import { splitFullPath } from '../helpers/splitFullPath';

import type { WorkspaceTab } from '../types';

/**
 * The place a path shows: its catalog destination, else its pathname.
 *
 * Destination ids never start with `/`, so the two kinds cannot collide. The
 * pathname fallback is what folds every Go box search into one Search tab —
 * `/search` has no catalog entry, and the query is not part of the place.
 */
export function placeKey(path: string): string {
  return destinationForPath(path)?.id ?? splitFullPath(path).pathname;
}

/**
 * The primary tab already holding `path`'s place, if any: the active tab when
 * it does, else the leftmost. Preferring the active tab keeps a click from
 * jumping off the tab the user is on.
 */
export function tabForPlace(
  tabs: WorkspaceTab[],
  primaryActiveTabId: string,
  path: string
): WorkspaceTab | undefined {
  const key = placeKey(path);
  const holders = tabs.filter(t => t.group === 'primary' && placeKey(t.path) === key);
  return holders.find(t => t.id === primaryActiveTabId) ?? holders[0];
}

/**
 * Collapse primary tabs that share a place down to one each — the active tab
 * where it is one of them, else the leftmost. The side pane is left alone: it
 * keeps a visible tab row with close buttons.
 */
export function dedupePrimaryTabs(
  tabs: WorkspaceTab[],
  primaryActiveTabId: string
): WorkspaceTab[] {
  const keep = new Map<string, string>();
  for (const tab of tabs.filter(t => t.group === 'primary')) {
    const key = placeKey(tab.path);
    if (!keep.has(key) || tab.id === primaryActiveTabId) keep.set(key, tab.id);
  }
  const kept = new Set(keep.values());
  return tabs.filter(t => t.group !== 'primary' || kept.has(t.id));
}
